/**
 * <boum-video> – stumme Produktvideos, die von selbst laufen, wenn man sie sieht.
 *
 * - Die MP4 wird erst 200 px vor dem Sichtbarwerden gesetzt (vorher 0 Bytes).
 * - Ab 50 % Sichtbarkeit läuft es, darunter pausiert es.
 * - Es läuft immer nur ein Video gleichzeitig.
 * - Wer „Bewegung reduzieren“ eingestellt hat oder Datensparen nutzt, sieht nur
 *   das Poster mit Play-Knopf.
 * - Wer selbst auf Pause drückt, bekommt kein Autoplay mehr für dieses Video.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = Boolean(navigator.connection && navigator.connection.saveData);
const autoplayAllowed = !reduceMotion && !saveData;
const smallScreen = window.matchMedia('(max-width: 749px)');

let current = null;

class BoumVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');
    if (!this.video) return;

    this.userPaused = false;
    this.toggle?.addEventListener('click', () => this.onToggle());
    this.video.addEventListener('playing', () => this.setPlaying(true));
    this.video.addEventListener('pause', () => this.setPlaying(false));

    this.loader = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.load();
          this.loader.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    this.loader.observe(this);

    this.watcher = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          if (autoplayAllowed && !this.userPaused) this.play();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    this.watcher.observe(this);
  }

  disconnectedCallback() {
    this.loader?.disconnect();
    this.watcher?.disconnect();
  }

  load() {
    if (this.video.src) return;
    const { srcSmall, srcLarge } = this.dataset;
    if (smallScreen.matches && srcSmall && srcSmall !== srcLarge) {
      // Die kleine Fassung ist aus dem Namensmuster abgeleitet – fehlt sie, große laden.
      this.video.addEventListener(
        'error',
        () => {
          this.video.src = srcLarge;
          if (current === this && !this.userPaused) this.video.play().catch(() => {});
        },
        { once: true }
      );
      this.video.src = srcSmall;
    } else {
      this.video.src = srcLarge;
    }
  }

  play() {
    this.load();
    if (current && current !== this) current.video.pause();
    current = this;
    this.video.play().catch(() => {
      /* Browser blockiert Autoplay – dann bleibt der Play-Knopf. */
    });
  }

  onToggle() {
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }

  setPlaying(playing) {
    this.classList.toggle('is-playing', playing);
    if (playing) this.classList.add('has-played');
    if (this.toggle) {
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
    }
  }
}

if (!customElements.get('boum-video')) customElements.define('boum-video', BoumVideo);
